import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import magic from '../lib/magic';

const Pricing = () => {
  const { user } = useAuth();
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      name: "Beginner",
      price: "£15",
      period: "/ month",
      priceId: "price_1QFz9DGOnDBMaozTzHqMSOGg",
      features: [
        "100 AI Generations",
        "High-Quality Images",
        "Multiple Download Formats (JPEG, PNG)",
        "Basic Customization (colors, simple text)",
        "Standard Processing Speed"
      ],
      gradient: "from-[#FF3366] to-[#FF6B98]",
      popular: false,
      credits: 100
    },
    {
      name: "Hobbyist",
      price: "£30",
      period: "/ month",
      priceId: "price_1QFz9PGOnDBMaozT7nvU8IAx",
      features: [
        "250 AI Generations",
        "Premium Quality Images (higher resolution)",
        "Access to 10 Additional Poster Sizes",
        "Portrait & Horizontal Options",
        "Standard Processing Speed",
        "Email Support (24-hour response)"
      ],
      gradient: "from-[#4F46E5] to-[#7C3AED]",
      popular: true,
      credits: 250
    },
    {
      name: "Artist",
      price: "£50",
      period: "/ month",
      priceId: "price_1QFz9ZGOnDBMaozTUden7I0a",
      features: [
        "500 AI Generations",
        "Maximum Quality Images (ultra-high resolution)",
        "Fastest Processing Speed",
        "Full Access to 10 Additional Poster Sizes",
        "Free Shipping on Poster Orders",
        "Access to Exclusive Templates",
        "Storage for Saved Projects",
        "Priority Support"
      ],
      gradient: "from-[#FF6B98] to-[#7C3AED]",
      popular: false,
      credits: 500
    }
  ];

  const handleSubscribe = useCallback(async (plan) => {
    try {
      setIsLoading(true);
      const button = document.activeElement;
      if (button) button.disabled = true;

      // Get the auth token
      const didToken = await magic.user.getIdToken();

      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${didToken}`
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          userEmail: user.email,
          planName: plan.name,
          credits: plan.credits
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error:', error);
      const button = document.activeElement;
      if (button) button.disabled = false;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF7FF] to-[#F5EFFF]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text">
              AI-to-Poster
            </Link>
            <div className="flex items-center space-x-6">
              {user && (
                <>
                  <div className="text-sm text-gray-600">
                    {user.email}
                  </div>
                  <Link 
                    to="/generator"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Generator
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.h1 
          className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Choose Your Plan
        </motion.h1>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative flex flex-col h-full ${
                hoveredPlan === index ? 'z-10' : 'z-0'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onHoverStart={() => setHoveredPlan(index)}
              onHoverEnd={() => setHoveredPlan(null)}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <motion.div
                  className="absolute -top-4 inset-x-0 flex justify-center z-10"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg">
                    Most Popular
                  </span>
                </motion.div>
              )}

              <motion.div
                className={`relative flex flex-col h-full p-6 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg transition-all duration-300
                  ${hoveredPlan === index ? 'shadow-2xl scale-105' : 'hover:shadow-xl hover:-translate-y-1'}`}
                animate={{
                  scale: hoveredPlan === index ? 1.05 : 1,
                  boxShadow: hoveredPlan === index ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)" : "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                }}
              >
                {/* Background Gradient */}
                <motion.div
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${plan.gradient} opacity-0 transition-opacity duration-300`}
                  animate={{ opacity: hoveredPlan === index ? 0.05 : 0 }}
                />

                <div className="relative">
                  <motion.h3 
                    className="text-xl font-semibold mb-2"
                    animate={{ 
                      color: hoveredPlan === index ? '#FF3366' : '#1F2937'
                    }}
                  >
                    {plan.name}
                  </motion.h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-gray-500 ml-1">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <motion.li
                        key={i}
                        className="flex items-start text-gray-600"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * i }}
                      >
                        <motion.svg
                          className="w-5 h-5 mr-2 text-green-500 mt-1 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          animate={{
                            scale: hoveredPlan === index ? [1, 1.2, 1] : 1,
                            transition: { duration: 0.2 }
                          }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </motion.svg>
                        <span className="text-sm">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto relative">
                  <motion.button
                    onClick={() => handleSubscribe(plan)}
                    className="block w-full py-3 px-6 text-center text-white bg-gradient-to-r from-[#FF3366] to-[#7C3AED] rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Subscribe Now'}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
        <motion.div
          className="absolute top-[-5%] right-[-5%] w-1/3 h-1/3 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full blur-3xl opacity-30"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute bottom-[-5%] left-[-5%] w-1/3 h-1/3 bg-gradient-to-tr from-blue-200 to-teal-200 rounded-full blur-3xl opacity-30"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>
    </div>
  );
};

export default Pricing;
